import React, { useEffect, useState, useCallback } from "react";
import { API } from "../lib/api";
import { toast } from "sonner";

const TOKEN_KEY = "alati_admin_token";
const getTok = () => localStorage.getItem(TOKEN_KEY);
const setTok = (t) => localStorage.setItem(TOKEN_KEY, t);
const clearTok = () => localStorage.removeItem(TOKEN_KEY);

const admin = async (path, opts = {}) => {
    const r = await fetch(`${API}/admin${path}`, {
        ...opts,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getTok()}`, ...(opts.headers || {}) },
    });
    if (r.status === 401 || r.status === 403) { clearTok(); throw new Error("Unauthorized"); }
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `Error ${r.status}`);
    return r.json();
};

const Login = ({ onOk }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setBusy(true);
        try {
            const r = await fetch(`${API}/admin/login`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password }),
            });
            if (!r.ok) throw new Error("Invalid email or password");
            const data = await r.json();
            setTok(data.token); onOk();
        } catch (e) { toast.error(e.message); } finally { setBusy(false); }
    };
    return (
        <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6">
            <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
                <p className="label">ALATI Admin</p>
                <h1 className="font-serif text-4xl mt-4">Sign in</h1>
                <div className="mt-8"><label className="label">Email</label>
                    <input className="alati-input mt-2" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" /></div>
                <div className="mt-6"><label className="label">Password</label>
                    <input className="alati-input mt-2" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" /></div>
                <button type="submit" disabled={busy} className="btn-primary w-full mt-10" data-testid="admin-login-submit">{busy ? "Signing in…" : "Sign In"}</button>
            </form>
        </div>
    );
};

const ProductEditor = ({ p, onSave, onDelete, onClose }) => {
    const [form, setForm] = useState({ ...p });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const save = async () => {
        try {
            await admin(`/products/${p.slug}`, { method: "PATCH", body: JSON.stringify({
                name: form.name, price: parseFloat(form.price), short_description: form.short_description,
                description: form.description, ingredients: form.ingredients,
                fragrance_status: form.fragrance_status, category: form.category, in_stock: form.in_stock,
                images: form.images, botanical_infusion: form.botanical_infusion,
            })});
            toast.success("Saved."); onSave();
        } catch (e) { toast.error(e.message); }
    };
    return (
        <div className="fixed inset-0 bg-black/40 z-50 overflow-y-auto" onClick={onClose}>
            <div className="bg-[#FAF9F6] max-w-2xl mx-auto my-10 p-8 border border-[#E3DED5]" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-6">
                    <h2 className="font-serif text-2xl">Edit: {p.name}</h2>
                    <button onClick={onClose} className="text-sm">Close</button>
                </div>
                {[
                    ["Name", "name", "text"], ["Price (AUD)", "price", "number"],
                    ["Short description", "short_description", "text"],
                    ["Category", "category", "text"],
                ].map(([label, k, t]) => (
                    <div className="mb-4" key={k}><label className="label">{label}</label>
                        <input className="alati-input mt-1" type={t} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} /></div>
                ))}
                <div className="mb-4"><label className="label">Description</label>
                    <textarea className="alati-input mt-1 resize-none" rows="4" value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
                <div className="mb-4"><label className="label">Ingredients (one per line)</label>
                    <textarea className="alati-input mt-1 resize-none" rows="5" value={(form.ingredients || []).join("\n")} onChange={(e) => set("ingredients", e.target.value.split("\n").filter(Boolean))} /></div>
                <div className="mb-4"><label className="label">Botanical infusion — colour note</label>
                    <input className="alati-input mt-1" value={form.botanical_infusion?.colour_note || ""} onChange={(e) => set("botanical_infusion", { ...(form.botanical_infusion || {}), colour_note: e.target.value })} /></div>
                <div className="mb-4"><label className="label">Fragrance</label>
                    <select className="alati-input mt-1" value={form.fragrance_status || "Unscented"} onChange={(e) => set("fragrance_status", e.target.value)}>
                        <option>Unscented</option><option>Essential Oils Only</option></select></div>
                <div className="mb-4"><label className="label">Stock</label>
                    <select className="alati-input mt-1" value={form.in_stock ? "in" : "out"} onChange={(e) => set("in_stock", e.target.value === "in")}>
                        <option value="in">In Stock</option><option value="out">Sold Out</option></select></div>
                <div className="mb-4"><label className="label">Image URLs (one per line)</label>
                    <textarea className="alati-input mt-1 resize-none" rows="3" value={(form.images || []).join("\n")} onChange={(e) => set("images", e.target.value.split("\n").filter(Boolean))} />
                    <p className="text-xs text-[#737562] mt-2">V1: paste image URLs (upload widget arrives in V2). Host new photos on your CDN or asset store, then paste the URLs here.</p></div>
                <div className="flex justify-between mt-8">
                    <button onClick={() => { if (window.confirm("Delete this soap?")) { admin(`/products/${p.slug}`, { method: "DELETE" }).then(() => { toast.success("Deleted"); onDelete(); }); } }} className="text-sm text-red-700">Delete</button>
                    <button onClick={save} className="btn-primary" data-testid="admin-save-product">Save Changes</button>
                </div>
            </div>
        </div>
    );
};

const Panel = () => {
    const [tab, setTab] = useState("dashboard");
    const [dash, setDash] = useState(null);
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [settings, setSettings] = useState(null);
    const [editing, setEditing] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [reviewFilter, setReviewFilter] = useState("pending");
    const [editingReview, setEditingReview] = useState(null);

    const loadReviews = useCallback(async (status) => {
        try {
            const s = status || reviewFilter;
            const data = await admin(`/reviews?status=${s}`);
            setReviews(data);
        } catch (e) { toast.error(e.message); }
    }, [reviewFilter]);

    const load = useCallback(async () => {
        try {
            setDash(await admin("/dashboard"));
            setProducts(await admin("/products"));
            setOrders(await admin("/orders"));
            setSettings(await admin("/settings"));
            // reviews are loaded by the [reviewFilter] effect below — don't fetch here or it overwrites the filter
        } catch (e) { toast.error(e.message); }
    }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadReviews(reviewFilter); }, [reviewFilter, loadReviews]);

    const saveSettings = async () => {
        try { setSettings(await admin("/settings", { method: "PUT", body: JSON.stringify(settings) })); toast.success("Settings saved"); }
        catch (e) { toast.error(e.message); }
    };
    const setOrderStatus = async (order_number, status) => {
        try { await admin(`/orders/${order_number}`, { method: "PATCH", body: JSON.stringify({ status }) }); setOrders(await admin("/orders")); toast.success("Order updated"); }
        catch (e) { toast.error(e.message); }
    };

    const patchReview = async (id, payload, successMsg) => {
        try {
            await admin(`/reviews/${id}`, { method: "PATCH", body: JSON.stringify(payload) });
            await loadReviews(reviewFilter);
            toast.success(successMsg || "Saved");
            setEditingReview(null);
        } catch (e) { toast.error(e.message); }
    };
    const deleteReview = async (id) => {
        if (!window.confirm("Delete this review permanently?")) return;
        try {
            await admin(`/reviews/${id}`, { method: "DELETE" });
            await loadReviews(reviewFilter);
            toast.success("Deleted");
        } catch (e) { toast.error(e.message); }
    };

    const tabs = [["dashboard", "Dashboard"], ["products", "Bars"], ["orders", "Orders"], ["reviews", "Reviews"], ["settings", "Shop Settings"]];

    return (
        <div className="bg-[#FAF9F6] min-h-screen" data-testid="admin-panel">
            <div className="border-b border-[#E3DED5] px-6 py-4 flex justify-between items-center bg-white sticky top-0 z-10">
                <h1 className="font-serif text-xl">ALATI Admin</h1>
                <div className="flex gap-6 text-[11px] uppercase tracking-[0.22em]">
                    {tabs.map(([id, label]) => (
                        <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
                            className={tab === id ? "text-[#1C1C1A]" : "text-[#737562] hover:text-[#1C1C1A]"}>{label}</button>
                    ))}
                    <button onClick={() => { clearTok(); window.location.reload(); }} className="text-red-700" data-testid="admin-logout">Log out</button>
                </div>
            </div>
            <div className="max-w-6xl mx-auto p-8">
                {tab === "dashboard" && dash && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="admin-dashboard">
                        {[["Orders today", dash.orders_today], ["Orders this month", dash.orders_this_month],
                          ["Revenue", `$${dash.revenue.toFixed(2)}`], ["Best-selling soap", dash.best_selling_soap || "—"]].map(([l, v]) => (
                            <div key={l} className="bg-white border border-[#E3DED5] p-6">
                                <p className="label">{l}</p><p className="font-serif text-3xl mt-3">{v}</p></div>
                        ))}
                    </div>
                )}

                {tab === "products" && (
                    <div data-testid="admin-products">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="font-serif text-2xl">Bars ({products.length})</h2>
                            <button onClick={async () => {
                                const slug = window.prompt("New product slug (lowercase, no spaces):"); if (!slug) return;
                                const name = window.prompt("Product name:"); if (!name) return;
                                try { await admin("/products", { method: "POST", body: JSON.stringify({ slug, name, price: 25, category: "tallow" }) });
                                    toast.success("Created — now edit it below."); load(); }
                                catch (e) { toast.error(e.message); }
                            }} className="btn-primary" data-testid="admin-new-product">Add New Bar</button>
                        </div>
                        <table className="w-full text-sm bg-white border border-[#E3DED5]">
                            <thead><tr className="border-b border-[#E3DED5]"><th className="p-3 text-left">Name</th><th className="p-3 text-left">Category</th><th className="p-3 text-left">Price</th><th className="p-3 text-left">Fragrance</th><th className="p-3 text-left">Stock</th><th></th></tr></thead>
                            <tbody>{products.map((p) => (
                                <tr key={p.slug} className="border-b border-[#E3DED5]" data-testid={`admin-row-${p.slug}`}>
                                    <td className="p-3 font-serif text-base">{p.name}</td><td className="p-3">{p.category}</td>
                                    <td className="p-3">${p.price.toFixed(2)}</td><td className="p-3">{p.fragrance_status}</td>
                                    <td className={`p-3 ${p.in_stock ? "" : "text-red-700"}`}>{p.in_stock ? "In stock" : "Sold out"}</td>
                                    <td className="p-3"><button onClick={() => setEditing(p)} className="text-[11px] uppercase tracking-[0.22em] alati-link">Edit</button></td>
                                </tr>))}</tbody>
                        </table>
                    </div>
                )}
                {editing && <ProductEditor p={editing} onSave={() => { setEditing(null); load(); }} onDelete={() => { setEditing(null); load(); }} onClose={() => setEditing(null)} />}

                {tab === "orders" && (
                    <div data-testid="admin-orders">
                        <h2 className="font-serif text-2xl mb-6">Orders ({orders.length})</h2>
                        <table className="w-full text-sm bg-white border border-[#E3DED5]">
                            <thead><tr className="border-b border-[#E3DED5]"><th className="p-3 text-left">Order #</th><th className="p-3 text-left">Method</th><th className="p-3 text-left">Customer</th><th className="p-3 text-left">Items</th><th className="p-3 text-left">Code</th><th className="p-3 text-left">Total</th><th className="p-3 text-left">Status</th></tr></thead>
                            <tbody>{orders.length === 0 && <tr><td className="p-6 text-[#737562]" colSpan={7}>No orders yet.</td></tr>}
                                {orders.map((o) => {
                                    const isPickup = o.delivery_method === "pickup";
                                    const pd = o.pickup_details || {};
                                    return (
                                <tr key={o.order_number} className="border-b border-[#E3DED5]" data-testid={`admin-order-${o.order_number}`}>
                                    <td className="p-3 font-mono">{o.order_number}</td>
                                    <td className="p-3">
                                        {isPickup ? (
                                            <span className="inline-block px-2 py-1 text-[10px] tracking-[0.18em] uppercase bg-[#F0EBE1] border border-[#C7C0B3]" data-testid={`admin-order-method-${o.order_number}`}>Local Pickup</span>
                                        ) : (
                                            <span className="text-[#737562] text-xs" data-testid={`admin-order-method-${o.order_number}`}>Ship</span>
                                        )}
                                    </td>
                                    <td className="p-3">
                                        {isPickup ? (
                                            <>
                                                {pd.full_name || o.full_name || "—"}<br />
                                                <span className="text-xs text-[#737562]">{pd.email || o.email}</span><br />
                                                <span className="text-xs text-[#737562]">Mobile: {pd.mobile || "—"}</span><br />
                                                <span className="text-xs text-[#737562]">{pd.suburb || "—"} {pd.postcode || ""}</span>
                                            </>
                                        ) : (
                                            <>
                                                {(o.ship_details?.full_name) || o.full_name || "—"}<br />
                                                <span className="text-xs text-[#737562]">{(o.ship_details?.email) || o.email}</span><br />
                                                <span className="text-xs text-[#737562]">Mobile: {(o.ship_details?.mobile) || o.mobile || "—"}</span><br />
                                                <span className="text-xs text-[#737562]">{(o.ship_details?.address_line1) || o.address || "—"}</span>
                                                {(o.ship_details?.address_line2) && <><br /><span className="text-xs text-[#737562]">{o.ship_details.address_line2}</span></>}
                                                <br />
                                                <span className="text-xs text-[#737562]">
                                                    {(o.ship_details?.suburb) || o.city || "—"}
                                                    {(o.ship_details?.state || o.state) ? `, ${o.ship_details?.state || o.state}` : ""}
                                                    {" "}{(o.ship_details?.postcode) || o.postcode || ""}
                                                </span><br />
                                                <span className="text-xs text-[#737562]">{(o.ship_details?.country) || o.country}</span>
                                            </>
                                        )}
                                    </td>
                                    <td className="p-3 text-xs">{(o.items || []).map((i) => `${i.name} ×${i.quantity}`).join(", ")}</td>
                                    <td className="p-3 text-xs" data-testid={`admin-order-code-${o.order_number}`}>{o.discount_code ? <strong>{o.discount_code}</strong> : "—"}</td>
                                    <td className="p-3">${(o.total || 0).toFixed(2)}</td>
                                    <td className="p-3">
                                        <select value={o.status} onChange={(e) => setOrderStatus(o.order_number, e.target.value)} className="text-sm">
                                            {["New", "Packed", "Posted", "Completed"].map((s) => <option key={s}>{s}</option>)}
                                            {!["New", "Packed", "Posted", "Completed"].includes(o.status) && <option value={o.status}>{o.status}</option>}
                                        </select>
                                    </td>
                                </tr>);})}</tbody>
                        </table>
                    </div>
                )}

                {tab === "reviews" && (
                    <div data-testid="admin-reviews">
                        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                            <h2 className="font-serif text-2xl">Reviews ({reviews.length})</h2>
                            <div className="flex gap-4 text-[11px] uppercase tracking-[0.22em]" data-testid="admin-reviews-filters">
                                {[["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["all", "All"]].map(([v, l]) => (
                                    <button key={v} onClick={() => setReviewFilter(v)} data-testid={`admin-reviews-filter-${v}`}
                                        className={reviewFilter === v ? "text-[#1C1C1A]" : "text-[#737562] hover:text-[#1C1C1A]"}>
                                        {l}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {reviews.length === 0 ? (
                            <p className="text-[#737562] p-6 bg-white border border-[#E3DED5]">No reviews in this bucket.</p>
                        ) : (
                            <div className="space-y-4">
                                {reviews.map((r) => (
                                    <div key={r.id} className="bg-white border border-[#E3DED5] p-6" data-testid={`admin-review-${r.id}`}>
                                        <div className="flex flex-wrap items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex flex-wrap gap-3 items-center text-xs uppercase tracking-[0.22em] text-[#737562]">
                                                    <span data-testid="admin-review-status" className={r.status === "approved" ? "text-[#6A7059]" : r.status === "rejected" ? "text-red-700" : ""}>{r.status}</span>
                                                    <span>&middot;</span>
                                                    <span data-testid="admin-review-product">{r.product_name}</span>
                                                    <span>&middot;</span>
                                                    <span>{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                                                    <span>&middot;</span>
                                                    <span>{r.created_at ? new Date(r.created_at).toLocaleString("en-AU") : ""}</span>
                                                </div>
                                                <p className="font-serif text-xl mt-3">{r.title}</p>
                                                <p className="text-[#4A4A46] mt-2 leading-relaxed">{r.body}</p>
                                                <p className="text-xs text-[#737562] mt-4">
                                                    <strong className="text-[#4A4A46]">{r.name}</strong> &middot; <span data-testid="admin-review-email">{r.email}</span>
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-2 min-w-[140px]">
                                                {r.status !== "approved" && (
                                                    <button onClick={() => patchReview(r.id, { status: "approved" }, "Approved")}
                                                        className="btn-primary text-xs" data-testid={`admin-review-approve-${r.id}`}>Approve</button>
                                                )}
                                                {r.status !== "rejected" && (
                                                    <button onClick={() => patchReview(r.id, { status: "rejected" }, "Rejected")}
                                                        className="btn-secondary text-xs" data-testid={`admin-review-reject-${r.id}`}>Reject</button>
                                                )}
                                                <button onClick={() => setEditingReview(r)}
                                                    className="text-[11px] uppercase tracking-[0.22em] alati-link" data-testid={`admin-review-edit-${r.id}`}>Edit</button>
                                                <button onClick={() => deleteReview(r.id)}
                                                    className="text-[11px] uppercase tracking-[0.22em] text-red-700 hover:underline" data-testid={`admin-review-delete-${r.id}`}>Delete</button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {editingReview && (
                            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditingReview(null)}>
                                <div className="bg-white max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-review-editor">
                                    <p className="label">Edit Review</p>
                                    <h3 className="font-serif text-2xl mt-2 mb-6">Correct typos</h3>
                                    <label className="label">Display name</label>
                                    <input className="alati-input mt-1 mb-4" value={editingReview.display_name || ""}
                                        onChange={(e) => setEditingReview({ ...editingReview, display_name: e.target.value })} />
                                    <label className="label">Title</label>
                                    <input className="alati-input mt-1 mb-4" value={editingReview.title || ""}
                                        onChange={(e) => setEditingReview({ ...editingReview, title: e.target.value })} />
                                    <label className="label">Body</label>
                                    <textarea className="alati-input mt-1 mb-4 resize-none" rows="6" value={editingReview.body || ""}
                                        onChange={(e) => setEditingReview({ ...editingReview, body: e.target.value })} />
                                    <div className="flex justify-end gap-3">
                                        <button onClick={() => setEditingReview(null)} className="text-xs uppercase tracking-[0.22em]">Cancel</button>
                                        <button onClick={() => patchReview(editingReview.id, {
                                            display_name: editingReview.display_name,
                                            title: editingReview.title,
                                            body: editingReview.body,
                                        }, "Saved")} className="btn-primary" data-testid="admin-review-save-edits">Save Changes</button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "settings" && settings && (
                    <div data-testid="admin-settings">
                        <h2 className="font-serif text-2xl mb-6">Shop Settings</h2>
                        <div className="bg-white border border-[#E3DED5] p-6 space-y-5 max-w-md">
                            {[["single_bar_price", "Single bar price (AUD)"], ["bundle_3", "Bundle of 3 (AUD)"], ["bundle_5", "Bundle of 5 (AUD)"], ["artisan_slab", "ALATI Artisan Slab (AUD)"], ["custom_slab_from", "Custom Artisan Slab from (AUD)"]].map(([k, l]) => (
                                <div key={k}><label className="label">{l}</label>
                                    <input className="alati-input mt-1" type="number" step="0.01" value={settings[k] ?? ""} onChange={(e) => setSettings({ ...settings, [k]: parseFloat(e.target.value) })} data-testid={`setting-${k}`} /></div>))}
                            <button onClick={saveSettings} className="btn-primary" data-testid="admin-save-settings">Save Settings</button>
                            <p className="text-xs text-[#737562]">Note: bundle/slab prices here are informational. Actual bundle math (15% for 3, 20% for 5) is computed server-side from bar prices. To change bar prices for the whole shop, edit each bar&rsquo;s price.</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const Admin = () => {
    const [authed, setAuthed] = useState(!!getTok());
    useEffect(() => {
        if (!getTok()) return;
        fetch(`${API}/admin/me`, { headers: { Authorization: `Bearer ${getTok()}` } })
            .then((r) => { if (!r.ok) { clearTok(); setAuthed(false); } });
    }, []);
    if (!authed) return <Login onOk={() => setAuthed(true)} />;
    return <Panel />;
};

export default Admin;
